import React, { useEffect, useState, useCallback, memo, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { 
  Layout, Typography, Form, Input, Select, Button, message, Space, Avatar, Upload, 
  Progress, Card, Row, Col, Divider, Badge, Spin, Alert, DatePicker, Modal, Dropdown, Switch
} from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import {
  UserOutlined, LogoutOutlined, UploadOutlined, HomeOutlined,
  IdcardOutlined, EditOutlined, CheckCircleOutlined, CameraOutlined,
  DeleteOutlined, SaveOutlined, SettingOutlined, BellOutlined,
  FileTextOutlined, SafetyOutlined, TeamOutlined,
  EnvironmentOutlined, MenuOutlined
} from '@ant-design/icons';
import { motion, AnimatePresence } from 'framer-motion';
import dayjs from 'dayjs';
import { QRCodeCanvas } from 'qrcode.react';
import './Profile.css';

const { Header, Content, Sider } = Layout;
const { Title, Text } = Typography;

// Constants
const FORM_SECTIONS = {
  PERSONAL: 'personal',
  ADDRESS: 'address',
  HOUSING: 'housing',
  VETERAN: 'veteran',
  SUPPORT: 'support',
  SETTINGS: 'settings'
};

const API_ENDPOINTS = {
  GET_PROFILE: '/backend/get_profile.php',
  UPDATE_PROFILE: '/backend/update_profile.php',
  LOGOUT: '/backend/logout.php',
  GET_USER_DATA: '/backend/get_user_data.php'
};

// Utility functions
const getCompletionColor = (percentage) => {
  if (percentage >= 80) return '#52c41a';
  if (percentage >= 60) return '#faad14';
  if (percentage >= 40) return '#fa8c16';
  return '#ff4d4f';
};

const calculateProfileCompletion = (data) => {
  const fields = Object.keys(data).filter(key => key !== 'profilePicture');
  const filledFields = fields.filter(key => 
    Array.isArray(data[key]) ? data[key].length > 0 : data[key]
  );
  return Math.round((filledFields.length / fields.length) * 100);
};

// Custom hooks
const useProfileData = () => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(API_ENDPOINTS.GET_PROFILE, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Erreur serveur : ${response.status}`);
      }

      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }

      const normalizedData = {
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        userName: data.userName || '',
        dateOfBirth: data.dateOfBirth || '',
        nhGuideNumber: data.nhGuideNumber || '',
        gender: data.gender || '',
        householdType: Array.isArray(data.householdType) 
          ? data.householdType 
          : (typeof data.householdType === 'string' ? data.householdType.split(',') : []),
        street: data.street || '',
        city: data.city || '',
        zipCode: data.zipCode || '',
        county: data.county || '',
        state: data.state || '',
        country: data.country || 'United States',
        housingStatus: data.housingStatus || '',
        profilePicture: data.profilePicture || '',
        veteranStatus: data.veteranStatus || '',
        veteranService: data.veteranService || '',
        branchOfMilitary: data.branchOfMilitary || '',
        militaryOperations: data.militaryOperations || '',
        militaryDischargeStatus: data.militaryDischargeStatus || '',
        militaryServiceStart: data.militaryServiceStart || '',
        militaryServiceEnd: data.militaryServiceEnd || '',
        supportSystemType: data.supportSystemType || '',
        supportSystemNumber: data.supportSystemNumber || '',
        recordType: data.recordType || '',
        housingLossCity: data.housingLossCity || '',
        housingLossZipCode: data.housingLossZipCode || '',
        mostTimeLocation: data.mostTimeLocation || '',
        connectToServices: data.connectToServices || '',
        preferredHousingResources: data.preferredHousingResources || '',
        cityToReceiveServices: data.cityToReceiveServices || '',
      };

      setProfileData(normalizedData);
      return normalizedData;
    } catch (err) {
      setError(err.message);
      message.error(`Échec du chargement du profil : ${err.message}`, 3);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { profileData, loading, error, fetchProfile };
};

const useProfileActions = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const updateProfile = useCallback(async (data) => {
    setLoading(true);
    try {
      const formData = new FormData();
      Object.keys(data).forEach(key => {
        if (key === 'profilePicture' && data[key] && data[key].file) {
          formData.append(key, data[key].file);
        } else {
          formData.append(key, Array.isArray(data[key]) ? data[key].join(',') : data[key] || '');
        }
      });

      const response = await fetch(API_ENDPOINTS.UPDATE_PROFILE, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Erreur serveur : ${response.status}`);
      }

      const result = await response.json();
      
      if (result.error) {
        throw new Error(result.error || 'Erreur lors de la mise à jour du profil.');
      }

      return result;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      const response = await fetch(API_ENDPOINTS.LOGOUT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Erreur serveur : ${response.status}`);
      }

      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error || 'Erreur lors de la déconnexion.');
      }

      message.success('Déconnexion réussie', 3);
      navigate('/login');
    } catch (error) {
      message.error(`Échec de la déconnexion : ${error.message}`, 3);
      throw error;
    }
  }, [navigate]);

  const removeProfilePicture = useCallback(async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('profilePicture', 'remove');

      const response = await fetch(API_ENDPOINTS.UPDATE_PROFILE, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Erreur serveur : ${response.status}`);
      }

      const result = await response.json();
      
      if (result.error) {
        throw new Error(result.error || 'Erreur lors de la suppression de la photo de profil.');
      }

      message.success('Photo de profil supprimée avec succès !', 3);
      return result;
    } catch (error) {
      message.error(`Échec de la suppression de la photo de profil : ${error.message}`, 3);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return { updateProfile, logout, removeProfilePicture, loading };
};

// Professional Header Component
const ProfessionalHeader = memo(({ 
  previewImage, 
  firstName, 
  lastName, 
  userName, 
  onLogout,
  loading,
  onToggleSidebar
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const menuItems = [
    {
      key: 'home',
      label: 'Home',
      icon: <UserOutlined />,
      onClick: () => {
        navigate('/home');
        setMenuOpen(false);
      },
    },
    
    {
      key: 'Contact',
      label: 'Contact',
      icon: <UserOutlined />,
      onClick: () => {
        navigate('/ContactForm');
        setMenuOpen(false);
      },
    },
    
    {
      key: 'services',
      label: 'Services',
      icon: <TeamOutlined />,
      onClick: () => {
        navigate('/ServiceProviders');
        setMenuOpen(false);
      },
    },
    {
      key: 'privacy',
      label: 'Privacy',
      icon: <SafetyOutlined />,
      onClick: () => {
        navigate('/DataPrivacy');
        setMenuOpen(false);
      },
    },
    
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      onClick: () => {
        onLogout();
        setMenuOpen(false);
      },
      style: { color: '#dc2626' },
    },
  ];

  return (
    <Header className="professional-header">
      <div className="header-content">
        <div className="header-left">
          <Space align="center" size={16}>
            <Button
              type="text"
              icon={<MenuOutlined />}
              className="mobile-menu-button"
              onClick={onToggleSidebar}
            />
            <div className="logo-container">
              <Title level={3} className="logo-text">NH Guide</Title>
            </div>
          </Space>
        </div>
        
        <div className="header-center">
          <Space size={32}>
            <Link to="/home" className="nav-item active">
              <UserOutlined /> Home
            </Link>
            <Link to="/ContactForm" className="nav-item active">
              <UserOutlined /> ContactForm
            </Link>
            <Link to="/ServiceProviders" className="nav-item">
              <TeamOutlined /> Services
            </Link>
            <Link to="/DataPrivacy" className="nav-item">
              <SafetyOutlined /> Privacy
            </Link>
          </Space>
        </div>

        <div className="header-right">
          <Space align="center" size={16}>
           
            
            <div className="user-profile">
              <Avatar
                src={previewImage}
                icon={<UserOutlined />}
                size={40}
                className="user-avatar"
              />
              <div className="user-info">
                <Text strong className="user-name">
                  {firstName && lastName ? `${firstName} ${lastName}` : 'User'}
                </Text>
                <Text className="user-role">
                  {userName ? `@${userName}` : 'Member'}
                </Text>
              </div>
            </div>
            
            <Button
              type="text"
              icon={<LogoutOutlined />}
              onClick={onLogout}
              loading={loading}
              className="logout-btn"
            >
              Logout
            </Button>

            <Dropdown
              menu={{ items: menuItems }}
              trigger={['click']}
              open={menuOpen}
              onOpenChange={setMenuOpen}
              overlayStyle={{ 
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '12px',
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.1)',
                minWidth: '200px',
                maxWidth: '90vw',
              }}
            >
              <Button
                type="text"
                icon={<MenuOutlined />}
                className="mobile-menu-button mobile-menu-toggle"
              />
            </Dropdown>
          </Space>
        </div>
      </div>
    </Header>
  );
});

// Professional Sidebar
const ProfessionalSidebar = memo(({ 
  currentSection, 
  onSectionChange, 
  profileCompletion,
  collapsed,
  onCollapse 
}) => {
  const menuItems = [
    { key: FORM_SECTIONS.PERSONAL, icon: <UserOutlined />, label: 'Personal Info' },
    { key: FORM_SECTIONS.ADDRESS, icon: <EnvironmentOutlined />, label: 'Address' },
    { key: FORM_SECTIONS.HOUSING, icon: <HomeOutlined />, label: 'Housing' },
    { key: FORM_SECTIONS.VETERAN, icon: <IdcardOutlined />, label: 'Veteran Info' },
    { key: FORM_SECTIONS.SUPPORT, icon: <TeamOutlined />, label: 'Support' },
    { key: FORM_SECTIONS.SETTINGS, icon: <SettingOutlined />, label: 'Settings' },
  ];

  const [qrVisible, setQrVisible] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrData, setQrData] = useState(null);

  const handleShowQr = useCallback(async () => {
    setQrLoading(true);
    try {
      const profileRes = await fetch(API_ENDPOINTS.GET_PROFILE, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!profileRes.ok) {
        throw new Error(`Failed to fetch profile data: ${profileRes.status}`);
      }

      const profileData = await profileRes.json();

      if (profileData.error) {
        throw new Error(profileData.error);
      }

      const userInfo = {
        nhGuideNumber: profileData.nhGuideNumber || profileData.nh_guide_number || 'N/A',
        userName: profileData.userName || profileData.username || 'N/A',
      };

      const baseUrl = window.location.origin;
      const qrDataPayload = {
        url: `${baseUrl}/userqrSupportCases?nhGuideNumber=${encodeURIComponent(userInfo.nhGuideNumber)}`,
        nhGuideNumber: userInfo.nhGuideNumber,
        userName: userInfo.userName,
      };

      setQrData(qrDataPayload);
      setQrVisible(true);
    } catch (err) {
      message.error(`Failed to generate QR code: ${err.message}`, 3);
    } finally {
      setQrLoading(false);
    }
  }, []);

  return (
    <Sider
      width={280}
      collapsed={collapsed}
      onCollapse={onCollapse}
      className="professional-sidebar"
      theme="light"
      breakpoint="lg"
      collapsedWidth={80}
      trigger={null}
    >
      <div className="sidebar-content">
        <div className="profile-completion-widget">
          <div className="completion-header">
            <Avatar 
              icon={<UserOutlined />}
              size={64}
              className="ant-avatar"
              src="components/nhguidelogo.jpg"
            />
            {!collapsed && <Text strong>Profile Completion</Text>}
          </div>
          <Progress
            type="circle"
            percent={profileCompletion}
            strokeColor={getCompletionColor(profileCompletion)}
            size={80}
            format={percent => `${percent}%`}
          />
          {!collapsed && (
            <Text className="completion-text">
              {profileCompletion >= 80 ? 'Excellent!' : 
               profileCompletion >= 60 ? 'Good progress' : 
               'Keep going!'}
            </Text>
          )}
        </div>

        <div className="menu-section">
          {!collapsed && <Text className="menu-title">NAVIGATION</Text>}
          <div className="menu-items">
            {menuItems.map(item => (
              <div
                key={item.key}
                className={`menu-item ${currentSection === item.key ? 'active' : ''}`}
                onClick={() => onSectionChange(item.key)}
              >
                <span className="menu-icon">{item.icon}</span>
                {!collapsed && <span className="menu-label">{item.label}</span>}
              </div>
            ))}
          </div>
        </div>

        {!collapsed && (
          <div className="quick-actions">
            <Text className="menu-title">QUICK ACTIONS</Text>
            <Button
              icon={<FileTextOutlined />}
              type="primary"
              style={{ marginTop: 12 }}
              loading={qrLoading}
              onClick={handleShowQr}
              block
            >
              Show QR Code
            </Button>
            <Modal
              title="Your NH Guide QR Code"
              open={qrVisible}
              onCancel={() => setQrVisible(false)}
              footer={null}
              centered
              className="qr-modal"
            >
              {qrData ? (
                <>
                  <QRCodeCanvas
                    value={qrData.url}
                    size={200}
                    level="H"
                    includeMargin
                    style={{ margin: '0 auto', display: 'block' }}
                  />
                  <Divider />
                  <Text strong>NH GUIDE Number:</Text> <Text>{qrData.nhGuideNumber}</Text><br />
                  <Text strong>Username:</Text> <Text>{qrData.userName}</Text><br />
                  <Text strong>Scan to access:</Text> <Text>Support history and service activities</Text>
                </>
              ) : (
                <Spin tip="Generating QR Code..." />
              )}
            </Modal>
          </div>
        )}
      </div>
    </Sider>
  );
});

// Form Field Component
const FormField = memo(({ 
  name, 
  label, 
  control, 
  rules, 
  errors, 
  isEditing, 
  type = 'input',
  options = [],
  placeholder,
  ...props 
}) => (
  <Form.Item
    label={label}
    validateStatus={errors[name] ? 'error' : ''}
    help={errors[name]?.message}
    className="form-field"
  >
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => {
        const commonProps = {
          ...field,
          disabled: !isEditing,
          placeholder,
          size: 'large',
          className: `form-control ${type === 'select' ? 'form-select' : 'form-input'}`,
          ...props
        };

        if (type === 'select') {
          return <Select {...commonProps} options={options} />;
        }
        
        if (type === 'date') {
          return (
            <DatePicker 
              {...commonProps} 
              value={field.value ? dayjs(field.value) : null}
              onChange={(date) => field.onChange(date ? date.format('YYYY-MM-DD') : '')}
              format="YYYY-MM-DD"
            />
          );
        }
        
        return <Input {...commonProps} />;
      }}
    />
  </Form.Item>
));

// Personal Information Section
const PersonalInfoSection = memo(({ 
  control, 
  errors, 
  isEditing, 
  previewImage, 
  onImageChange, 
  onRemoveImage 
}) => (
  <div className="form-section">
    <div className="section-header">
      <Title level={3}>
        <UserOutlined /> Personal Information
      </Title>
    </div>

    <Card className="form-card">
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={8}>
          <div className="profile-picture-section">
            <div className="avatar-container">
              <Avatar
                src={previewImage}
                icon={<UserOutlined />}
                size={100}
                className="profile-avatar"
              />
              {isEditing && (
                <div className="avatar-overlay">
                  <Upload
                    beforeUpload={(file) => {
                      const isImage = file.type.startsWith('image/');
                      if (!isImage) {
                        message.error('Veuillez sélectionner un fichier image !', 3);
                        return false;
                      }
                      const isLt2M = file.size / 1024 / 1024 < 2;
                      if (!isLt2M) {
                        message.error('L\'image doit être inférieure à 2 Mo !', 3);
                        return false;
                      }
                      return false;
                    }}
                    onChange={onImageChange}
                    accept="image/*"
                    showUploadList={false}
                  >
                    <Button type="primary" icon={<CameraOutlined />} size="small">
                      Change
                    </Button>
                  </Upload>
                </div>
              )}
            </div>
            
            {isEditing && (
              <div className="avatar-actions">
                <Upload
                  beforeUpload={(file) => {
                    const isImage = file.type.startsWith('image/');
                    if (!isImage) {
                      message.error('Veuillez sélectionner un fichier image !', 3);
                      return false;
                    }
                    const isLt2M = file.size / 1024 / 1024 < 2;
                    if (!isLt2M) {
                      message.error('L\'image doit être inférieure à 2 Mo !', 3);
                      return false;
                    }
                    return false;
                  }}
                  onChange={onImageChange}
                  accept="image/*"
                  showUploadList={false}
                >
                  <Button icon={<UploadOutlined />} block>
                    Upload New Photo
                  </Button>
                </Upload>
                {previewImage && (
                  <Button 
                    danger 
                    icon={<DeleteOutlined />} 
                    onClick={onRemoveImage}
                    block
                  >
                    Remove Photo
                  </Button>
                )}
              </div>
            )}
          </div>
        </Col>
        
        <Col xs={24} lg={16}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <FormField
                name="firstName"
                label="First Name"
                control={control}
                rules={{ required: 'First Name is required' }}
                errors={errors}
                isEditing={isEditing}
                placeholder="Enter your first name"
              />
            </Col>
            <Col xs={24} md={12}>
              <FormField
                name="lastName"
                label="Last Name"
                control={control}
                rules={{ required: 'Last Name is required' }}
                errors={errors}
                isEditing={isEditing}
                placeholder="Enter your last name"
              />
            </Col>
            <Col xs={24} md={12}>
              <FormField
                name="userName"
                label="Username"
                control={control}
                rules={{ required: 'Username is required' }}
                errors={errors}
                isEditing={isEditing}
                placeholder="Choose a username"
              />
            </Col>
            <Col xs={24} md={12}>
              <FormField
                name="dateOfBirth"
                label="Date of Birth"
                control={control}
                rules={{ required: 'Date of Birth is required' }}
                errors={errors}
                isEditing={isEditing}
                type="date"
                placeholder="Select date of birth"
              />
            </Col>
            <Col xs={24} md={12}>
              <FormField
                name="nhGuideNumber"
                label="NH GUIDE Number"
                control={control}
                errors={errors}
                isEditing={false}
                placeholder="Auto-generated"
              />
            </Col>
            <Col xs={24} md={12}>
              <FormField
                name="gender"
                label="Gender"
                control={control}
                rules={{ required: 'Gender is required' }}
                errors={errors}
                isEditing={isEditing}
                type="select"
                placeholder="Select gender"
                options={[
                  { value: 'male', label: 'Male' },
                  { value: 'female', label: 'Female' },
                  { value: 'Non-binary', label: 'Non-binary' },
                  { value: 'Trans-female', label: 'Trans-female' },
                  { value: 'Trans-male', label: 'Trans-male' },
                  { value: 'Culturally Specific Gender Identity', label: 'Culturally Specific Gender Identity' },
                  { value: 'Prefer-not-to-answer', label: 'Prefer not to answer' },
                ]}
              />
            </Col>
            <Col xs={24}>
              <FormField
                name="householdType"
                label="Race and Ethnicity"
                control={control}
                rules={{ required: 'Race and Ethnicity is required' }}
                errors={errors}
                isEditing={isEditing}
                type="select"
                mode="multiple"
                maxTagCount={3}
                placeholder="Select Race and Ethnicity"
                options={[ 
                  { value: 'American Indian, Alaska Native, or Indigenous', label: 'American Indian, Alaska Native, or Indigenous' },
                  { value: 'Asian or Asian American', label: 'Asian or Asian American' },
                  { value: 'Black, African American, or African', label: 'Black, African American, or African' },
                  { value: 'Hispanic/Latina/e/o', label: 'Hispanic/Latina/e/o' },
                  { value: 'Middle Eastern or North African', label: 'Middle Eastern or North African' },
                  { value: 'Native Hawaiian or Pacific Islander', label: 'Native Hawaiian or Pacific Islander' },
                  { value: 'White', label: 'White' },
                  { value: 'Prefer not to Answer', label: 'Prefer not to Answer' },
                ]}
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </Card>
  </div>
));

// Address Information Section
const AddressSection = memo(({ control, errors, isEditing }) => (
  <div className="form-section">
    <div className="section-header">
      <Title level={3}>
        <EnvironmentOutlined /> Address /Location Information
      </Title>
      <Text>Provide your current location or address details.</Text>
    </div>

    <Card className="form-card">
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <FormField
            name="street"
            label="Street Address"
            control={control}
            rules={{ required: 'Street address is required' }}
            errors={errors}
            isEditing={isEditing}
            placeholder="Enter street number and name"
          />
        </Col>
        <Col xs={24}>
          <FormField
            name="city"
            label="City"
            control={control}
            rules={{ required: 'City is required' }}
            errors={errors}
            isEditing={isEditing}
            placeholder="Enter city"
          />
        </Col>
        <Col xs={24}>
          <FormField
            name="zipCode"
            label="ZIP Code"
            control={control}
            rules={{ required: 'ZIP Code is required' }}
            errors={errors}
            isEditing={isEditing}
            placeholder="Enter ZIP code"
          />
        </Col>
        <Col xs={24}>
          <FormField
            name="county"
            label="County"
            control={control}
            rules={{ required: 'County is required' }}
            errors={errors}
            isEditing={isEditing}
            placeholder="Enter county"
          />
        </Col>
        <Col xs={24}>
          <FormField
            name="state"
            label="State"
            control={control}
            rules={{ required: 'State is required' }}
            errors={errors}
            isEditing={isEditing}
            type="select"
            placeholder="Select state"
            options={[ 
              { value: 'AL', label: 'Alabama' },
              { value: 'AK', label: 'Alaska' },
              { value: 'AZ', label: 'Arizona' },
              { value: 'AR', label: 'Arkansas' },
              { value: 'CA', label: 'California' },
              { value: 'CO', label: 'Colorado' },
              { value: 'CT', label: 'Connecticut' },
              { value: 'DE', label: 'Delaware' },
              { value: 'FL', label: 'Florida' },
              { value: 'GA', label: 'Georgia' },
              { value: 'HI', label: 'Hawaii' },
              { value: 'ID', label: 'Idaho' },
              { value: 'IL', label: 'Illinois' },
              { value: 'IN', label: 'Indiana' },
              { value: 'IA', label: 'Iowa' },
              { value: 'KS', label: 'Kansas' },
              { value: 'KY', label: 'Kentucky' },
              { value: 'LA', label: 'Louisiana' },
              { value: 'ME', label: 'Maine' },
              { value: 'MD', label: 'Maryland' },
              { value: 'MA', label: 'Massachusetts' },
              { value: 'MI', label: 'Michigan' },
              { value: 'MN', label: 'Minnesota' },
              { value: 'MS', label: 'Mississippi' },
              { value: 'MO', label: 'Missouri' },
              { value: 'MT', label: 'Montana' },
              { value: 'NE', label: 'Nebraska' },
              { value: 'NV', label: 'Nevada' },
              { value: 'NH', label: 'New Hampshire' },
              { value: 'NJ', label: 'New Jersey' },
              { value: 'NM', label: 'New Mexico' },
              { value: 'NY', label: 'New York' },
              { value: 'NC', label: 'North Carolina' },
              { value: 'ND', label: 'North Dakota' },
              { value: 'OH', label: 'Ohio' },
              { value: 'OK', label: 'Oklahoma' },
              { value: 'OR', label: 'Oregon' },
              { value: 'PA', label: 'Pennsylvania' },
              { value: 'RI', label: 'Rhode Island' },
              { value: 'SC', label: 'South Carolina' },
              { value: 'SD', label: 'South Dakota' },
              { value: 'TN', label: 'Tennessee' },
              { value: 'TX', label: 'Texas' },
              { value: 'UT', label: 'Utah' },
              { value: 'VT', label: 'Vermont' },
              { value: 'VA', label: 'Virginia' },
              { value: 'WA', label: 'Washington' },
              { value: 'WV', label: 'West Virginia' },
              { value: 'WI', label: 'Wisconsin' },
              { value: 'WY', label: 'Wyoming' },
            ]}
          />
        </Col>
        <Col xs={24}>
          <FormField
            name="country"
            label="Country"
            control={control}
            rules={{ required: 'Country is required' }}
            errors={errors}
            isEditing={isEditing}
            type="select"
            placeholder="Select country"
            options={[ 
              { value: 'United States', label: 'United States' },
              { value: 'Canada', label: 'Canada' },
              { value: 'Mexico', label: 'Mexico' },
              { value: 'United Kingdom', label: 'United Kingdom' },
            ]}
          />
        </Col>
      </Row>
    </Card>
  </div>
));

// Housing Status Section
const HousingSection = memo(({ control, errors, isEditing, connectToServices }) => (
  <div className="form-section">
    <div className="section-header">
      <Title level={3}>
        <HomeOutlined /> Housing Status
      </Title>
      <Text>Information about your current housing situation and preferences.</Text>
    </div>

    <Card className="form-card">
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <FormField
            name="housingStatus"
            label="Current Housing Status"
            control={control}
            rules={{ required: 'Current Housing Status is required' }}
            errors={errors}
            isEditing={isEditing}
            type="select"
            placeholder="Select your current housing situation"
            options={[ 
              { value: 'Unhoused and staying in an automobile', label: 'Unhoused and staying in an automobile' },
              { value: 'Unhoused and staying in a place not meant for human habitation', label: 'Unhoused and staying in a place not meant for human habitation' },
              { value: 'Unhoused and staying temporarily with someone', label: 'Unhoused and staying temporarily with someone' },
              { value: 'Unhoused and in an emergency shelter/transitional housing', label: 'Unhoused and in an emergency shelter/transitional housing' },
              { value: 'Still housed but will be unhoused in less than 7 days', label: 'Still housed but will be unhoused in less than 7 days' },
              { value: 'Still housed but will be unhoused in less than 30 days', label: 'Still housed but will be unhoused in less than 30 days' },
              { value: 'Still housed but will be unhoused in 30 - 60 days', label: 'Still housed but will be unhoused in 30 - 60 days' },
              { value: 'Regained Housing after being homeless/unhoused', label: 'Regained Housing after being homeless/unhoused' },
              { value: 'Other', label: 'Other' },
            ]}
          />
        </Col>
        
        <Col xs={24}>
          <Divider orientation="left">Housing Loss Information</Divider>
        </Col>
        
        <Col xs={24}>
          <FormField
            name="housingLossCity"
            label="Where did you lose your housing? (City)"
            control={control}
            rules={{ required: 'City is required' }}
            errors={errors}
            isEditing={isEditing}
            placeholder="Enter city"
          />
        </Col>
        
        <Col xs={24}>
          <FormField
            name="housingLossZipCode"
            label="Where did you lose your housing? (ZIP Code)"
            control={control}
            rules={{ required: 'ZIP Code is required' }}
            errors={errors}
            isEditing={isEditing}
            placeholder="Enter ZIP code"
          />
        </Col>
        
        <Col xs={24}>
          <FormField
            name="mostTimeLocation"
            label="Where are you located most of the time?"
            control={control}
            rules={{ required: 'Location is required' }}
            errors={errors}
            isEditing={isEditing}
            type="select"
            placeholder="Select your primary location"
            options={[ 
              { value: 'San Francisco, 94102', label: 'San Francisco, 94102' },
              { value: 'New York, 10001', label: 'New York, 10001' },
              { value: 'Houston, 77002', label: 'Houston, 77002' },
              { value: 'Los Angeles, 90001', label: 'Los Angeles, 90001' },
              { value: 'Chicago, 60601', label: 'Chicago, 60601' },
            ]}
          />
        </Col>
        
        <Col xs={24}>
          <FormField
            name="connectToServices"
            label="Would you like information to connect to services and support near where you're located?"
            control={control}
            rules={{ required: 'This field is required' }}
            errors={errors}
            isEditing={isEditing}
            type="select"
            placeholder="Select your preference"
            options={[ 
              { value: 'Yes', label: 'Yes, I want information to connect to services near where I am located' },
              { value: 'No', label: 'No, not at this time' },
            ]}
          />
        </Col>
        
        {connectToServices === 'No' && (
          <>
            <Col xs={24}>
              <Divider orientation="left">Service Preferences</Divider>
            </Col>
            <Col xs={24}>
              <FormField
                name="preferredHousingResources"
                label="Preferred Housing Resources"
                control={control}
                errors={errors}
                isEditing={isEditing}
                placeholder="Specify preferred resources"
              />
            </Col>
            <Col xs={24}>
              <FormField
                name="cityToReceiveServices"
                label="City to Receive Services"
                control={control}
                errors={errors}
                isEditing={isEditing}
                placeholder="Preferred service location"
              />
            </Col>
          </>
        )}
      </Row>
    </Card>
  </div>
));

// Veteran Information Section
const VeteranSection = memo(({ control, errors, isEditing, veteranStatus }) => (
  <div className="form-section">
    <div className="section-header">
      <Title level={3}>
        <IdcardOutlined /> Veteran Information
      </Title>
      <Text>Military service history and veteran status information.</Text>
    </div>

    <Card className="form-card">
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <FormField
            name="veteranStatus"
            label="Veteran Status"
            control={control}
            rules={{ required: 'Veteran Status is required' }}
            errors={errors}
            isEditing={isEditing}
            type="select"
            placeholder="Select your veteran status"
            options={[ 
              { value: 'Yes', label: 'Yes, I am a veteran' },
              { value: 'No', label: 'No, I am not a veteran' },
              { value: 'Prefer not to answer', label: 'Prefer not to answer' },
            ]}
          />
        </Col>
        
        {veteranStatus === 'Yes' && (
          <>
            <Col xs={24}>
              <Divider orientation="left">Military Service Details</Divider>
            </Col>
            
            <Col xs={24}>
              <FormField
                name="veteranService"
                label="Armed Forces Service"
                control={control}
                rules={{ required: 'Armed Forces Service is required' }}
                errors={errors}
                isEditing={isEditing}
                type="select"
                placeholder="Select service branch"
                options={[ 
                  { value: 'Air Force', label: 'Air Force' },
                  { value: 'Army', label: 'Army' },
                  { value: 'Coast Guard', label: 'Coast Guard' },
                  { value: 'Marines', label: 'Marines' },
                  { value: 'Navy', label: 'Navy' },
                  { value: 'Space Force', label: 'Space Force' },
                ]}
              />
            </Col>
            
            <Col xs={24}>
              <FormField
                name="branchOfMilitary"
                label="Branch of Military"
                control={control}
                rules={{ required: 'Branch of Military is required' }}
                errors={errors}
                isEditing={isEditing}
                type="select"
                placeholder="Select military branch"
                options={[ 
                  { value: 'Air Force', label: 'Air Force' },
                  { value: 'Army', label: 'Army' },
                  { value: 'Coast Guard', label: 'Coast Guard' },
                  { value: 'Marines', label: 'Marines' },
                  { value: 'Navy', label: 'Navy' },
                  { value: 'Space Force', label: 'Space Force' },
                ]}
              />
            </Col>
            
            <Col xs={24}>
              <FormField
                name="militaryOperations"
                label="Military Operations"
                control={control}
                rules={{ required: 'Military Operations is required' }}
                errors={errors}
                isEditing={isEditing}
                type="select"
                placeholder="Select military operations"
                options={[ 
                  { value: 'Iraq (New Dawn)', label: 'Iraq (New Dawn)' },
                  { value: 'Iraq (Iraqi Freedom)', label: 'Iraq (Iraqi Freedom)' },
                  { value: 'Afghanistan', label: 'Afghanistan' },
                  { value: 'Persian Gulf War (Operation Desert Storm) 1990 - 1991', label: 'Persian Gulf War (Operation Desert Storm) 1990 - 1991' },
                  { value: 'Vietnam War', label: 'Vietnam War' },
                  { value: 'Korean War', label: 'Korean War' },
                  { value: 'World War II', label: 'World War II' },
                  { value: 'Other Operations', label: 'Other Operations' },
                  { value: 'Prefer not to answer', label: 'Prefer not to answer' },
                ]}
              />
            </Col>
            
            <Col xs={24}>
              <FormField
                name="militaryDischargeStatus"
                label="Military Discharge Status"
                control={control}
                rules={{ required: 'Military Discharge Status is required' }}
                errors={errors}
                isEditing={isEditing}
                type="select"
                placeholder="Select discharge status"
                options={[ 
                  { value: 'Honorable', label: 'Honorable' },
                  { value: 'Other than Honorable Conditions', label: 'Other than Honorable Conditions' },
                  { value: 'Dishonorable', label: 'Dishonorable' },
                  { value: 'Prefer not to answer', label: 'Prefer not to answer' },
                ]}
              />
            </Col>
            
            <Col xs={24}>
              <FormField
                name="militaryServiceStart"
                label="Military Service Start Date"
                control={control}
                rules={{ required: 'Military Service Start date is required' }}
                errors={errors}
                isEditing={isEditing}
                type="date"
                placeholder="Select start date"
              />
            </Col>
            
            <Col xs={24}>
              <FormField
                name="militaryServiceEnd"
                label="Military Service End Date"
                control={control}
                rules={{ required: 'Military Service End date is required' }}
                errors={errors}
                isEditing={isEditing}
                type="date"
                placeholder="Select end date"
              />
            </Col>
          </>
        )}
      </Row>
    </Card>
  </div>
));

// Support System Section
const SupportSection = memo(({ control, errors, isEditing, supportSystemType }) => (
  <div className="form-section">
    <div className="section-header">
      <Title level={3}>
        <TeamOutlined /> Support System
      </Title>
      <Text>Information about your current support systems and assistance programs.</Text>
    </div>

    <Card className="form-card">
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Alert
            message="Support System Information"
            description="Please provide information about your current support systems and assistance programs to help us better serve you."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        </Col>
        
        <Col xs={24}>
          <FormField
            name="supportSystemType"
            label="Support System Type"
            control={control}
            rules={{ required: 'Support System Type is required' }}
            errors={errors}
            isEditing={isEditing}
            type="select"
            placeholder="Select Support System Type"
            options={[ 
              { value: 'Disability Services', label: 'Disability Services (SSI, SSDI)' },
              { value: 'Healthcare/Medical', label: 'Healthcare/Medical' },
              { value: 'Housing Crisis Response', label: 'Housing Crisis Response (HMIS)' },
              { value: 'Social Services', label: 'Social Services (Cal Fresh)' },
              { value: 'Other', label: 'Other' },
            ]}
          />
        </Col>
        
        <Col xs={24}>
          <FormField
            name="supportSystemNumber"
            label="Support System Number"
            control={control}
            rules={{ required: 'Support System Number is required' }}
            errors={errors}
            isEditing={isEditing}
            placeholder="Enter Record Number"
          />
        </Col>
        
        {supportSystemType === 'Record type' && (
          <Col xs={24}>
            <FormField
              name="recordType"
              label="Record Type"
              control={control}
              rules={{ required: 'Record Type is required' }}
              errors={errors}
              isEditing={isEditing}
              type="select"
              placeholder="Select record type"
              options={[ 
                { value: 'HMIS', label: 'HMIS' },
                { value: 'Other', label: 'Other' },
              ]}
            />
          </Col>
        )}
      </Row>
    </Card>
  </div>
));

// Settings Section
const SettingsSection = memo(() => (
  <div className="form-section">
    <div className="section-header">
      <Title level={3}>
        <SettingOutlined /> Account Settings
      </Title>
      <Text>Manage your account preferences and privacy settings.</Text>
    </div>

    <Card className="form-card">
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <div className="setting-item">
            <div className="setting-info">
              <Text strong>Email Notifications</Text>
              <br />
              <Text type="secondary">Receive updates about your profile and services</Text>
            </div>
            <Switch defaultChecked />
          </div>
        </Col>
        
        <Col xs={24}>
          <div className="setting-item">
            <div className="setting-info">
              <Text strong>SMS Notifications</Text>
              <br />
              <Text type="secondary">Receive text messages for important updates</Text>
            </div>
            <Switch />
          </div>
        </Col>
        
        <Col xs={24}>
          <div className="setting-item">
            <div className="setting-info">
              <Text strong>Profile Visibility</Text>
              <br />
              <Text type="secondary">Allow service providers to view your profile</Text>
            </div>
            <Switch defaultChecked />
          </div>
        </Col>
        
       
      </Row>
    </Card>
  </div>
));

// Main Profile Component
const Profile = () => {
  const { control, handleSubmit, formState: { errors }, reset, watch, getValues, setValue } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      userName: '',
      dateOfBirth: '',
      nhGuideNumber: '',
      gender: '',
      householdType: [],
      street: '',
      city: '',
      zipCode: '',
      county: '',
      state: '',
      country: 'United States',
      housingStatus: '',
      profilePicture: '',
      veteranStatus: '',
      veteranService: '',
      branchOfMilitary: '',
      militaryOperations: '',
      militaryDischargeStatus: '',
      militaryServiceStart: '',
      militaryServiceEnd: '',
      supportSystemType: '',
      supportSystemNumber: '',
      recordType: '',
      housingLossCity: '',
      housingLossZipCode: '',
      mostTimeLocation: '',
      connectToServices: '',
      preferredHousingResources: '',
      cityToReceiveServices: '',
    },
  });

  const [previewImage, setPreviewImage] = useState(null);
  const [currentSection, setCurrentSection] = useState(FORM_SECTIONS.PERSONAL);
  const [profileCompletion, setProfileCompletion] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(window.innerWidth <= 768);

  const { profileData, loading: fetchLoading, fetchProfile } = useProfileData();
  const { updateProfile, logout, removeProfilePicture, loading: actionLoading } = useProfileActions();

  const watchedValues = watch();
  const veteranStatus = watch('veteranStatus');
  const connectToServices = watch('connectToServices');
  const supportSystemType = watch('supportSystemType');

  const containerVariants = useMemo(() => ({
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.4, staggerChildren: 0.1 } }
  }), []);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const data = await fetchProfile();
        reset(data);
        setPreviewImage(data.profilePicture || null);
        setProfileCompletion(calculateProfileCompletion(data));
      } catch (error) {
        setStatusMessage({ type: 'error', text: error.message });
      }
    };

    loadProfile();
  }, [fetchProfile, reset]);

  useEffect(() => {
    setProfileCompletion(calculateProfileCompletion(watchedValues));
  }, [watchedValues]);

  const onSubmit = useCallback(async (data) => {
    try {
      const result = await updateProfile(data);
      if (result.profilePicture) {
        setPreviewImage(result.profilePicture);
      }
      message.success('Profil mis à jour avec succès !', 3);
      setIsEditing(false);
    } catch (error) {
      message.error(`Échec de la mise à jour du profil : ${error.message}`, 3);
    }
  }, [updateProfile]);

  const handleImageChange = useCallback(({ file }) => {
    if (file) {
      if (previewImage && previewImage.startsWith('blob:')) {
        URL.revokeObjectURL(previewImage);
      }
      const newPreview = URL.createObjectURL(file);
      setPreviewImage(newPreview);
      setValue('profilePicture', { file });
    }
  }, [previewImage, setValue]);

  const handleRemoveImage = useCallback(async () => {
    try {
      await removeProfilePicture();
      if (previewImage && previewImage.startsWith('blob:')) {
        URL.revokeObjectURL(previewImage);
      }
      setPreviewImage(null);
      setValue('profilePicture', null);
    } catch (error) {
      // Error handled in removeProfilePicture
    }
  }, [previewImage, removeProfilePicture, setValue]);

  const handleEditToggle = useCallback(() => {
    if (isEditing) {
      handleSubmit(onSubmit)();
    } else {
      setIsEditing(true);
    }
  }, [isEditing, handleSubmit, onSubmit]);

  const handleToggleSidebar = useCallback(() => {
    setSidebarCollapsed(prev => !prev);
  }, []);

  const renderSection = () => {
    switch (currentSection) {
      case FORM_SECTIONS.PERSONAL:
        return (
          <PersonalInfoSection 
            control={control} 
            errors={errors} 
            isEditing={isEditing}
            previewImage={previewImage}
            onImageChange={handleImageChange}
            onRemoveImage={handleRemoveImage}
          />
        );
      case FORM_SECTIONS.ADDRESS:
        return <AddressSection control={control} errors={errors} isEditing={isEditing} />;
      case FORM_SECTIONS.HOUSING:
        return <HousingSection control={control} errors={errors} isEditing={isEditing} connectToServices={connectToServices} />;
      case FORM_SECTIONS.VETERAN:
        return <VeteranSection control={control} errors={errors} isEditing={isEditing} veteranStatus={veteranStatus} />;
      case FORM_SECTIONS.SUPPORT:
        return <SupportSection control={control} errors={errors} isEditing={isEditing} supportSystemType={supportSystemType} />;
      case FORM_SECTIONS.SETTINGS:
        return <SettingsSection />;
      default:
        return null;
    }
  };

  return (
    <Layout className="profile-layout">
      <ProfessionalHeader
        previewImage={previewImage}
        firstName={getValues('firstName')}
        lastName={getValues('lastName')}
        userName={getValues('userName')}
        onLogout={logout}
        loading={actionLoading}
        onToggleSidebar={handleToggleSidebar}
      />
      
      <Layout>
        <ProfessionalSidebar
          currentSection={currentSection}
          onSectionChange={setCurrentSection}
          profileCompletion={profileCompletion}
          collapsed={sidebarCollapsed}
          onCollapse={setSidebarCollapsed}
        />
        
        <Content className="profile-content">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="content-container"
          >
            {fetchLoading ? (
              <Spin tip="Chargement du profil..." />
            ) : (
              <>
                {statusMessage && (
                  <Alert
                    message={statusMessage.text}
                    type={statusMessage.type}
                    showIcon
                    closable
                    onClose={() => setStatusMessage(null)}
                    style={{ marginBottom: 16 }}
                  />
                )}
                
                <div className="content-header">
                  <Button
                    type={isEditing ? 'primary' : 'default'}
                    icon={isEditing ? <SaveOutlined /> : <EditOutlined />}
                    onClick={handleEditToggle}
                    loading={actionLoading}
                    size="large"
                  >
                    {isEditing ? 'Save Profile' : 'Edit Profile'}
                  </Button>
                </div>
                
                <AnimatePresence>
                  {renderSection()}
                </AnimatePresence>
              </>
            )}
          </motion.div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default Profile;